import { useMemo } from 'react';
import { Box, Card, Title, Radio, Link, Icon } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { CreateArTaxRulesResponseDto } from '@tiendanube/common';
import ModalAside from 'App/components/lab/ModalAside';
import { toStatusType } from 'App/rtk';
import { HeaderTop, CancelAndConfirmButtons } from 'commons/components';
import { useForm, useModal, useToastStatus } from 'commons/hooks';
import { useTranslationAccount } from 'domains/Account/hooks';
import {
  SelectedProductInterface,
  SelectProductList,
} from 'domains/Catalog/Products/components';
import {
  useCreateArTaxRulesMutation,
  useUpdateArTaxRulesMutation,
} from 'domains/Invoices/invoicesApi';
import { useTaxExceptionsContext } from '../../ProductsTaxExceptions';
import {
  formatToSelectedProduct,
  initialValues,
  unFormatToSelectedProduct,
} from '../../utils/util';
import SelectedListProducts from '../SelectedListProducts';
import TaxRateInput from '../TaxRateInput';

interface TaxExceptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingItem?: CreateArTaxRulesResponseDto | null;
}

function TaxExceptionsModal({
  isOpen,
  onClose,
  editingItem = null,
}: TaxExceptionsModalProps) {
  const t = useTranslationAccount('businessDataPage.taxesWithExceptions.modal');
  const { productIdsWithExceptions } = useTaxExceptionsContext();

  const [isShowModal, openModal, closeModal] = useModal();
  const [createArTaxRules, { status: createArTaxRulesStatus }] =
    useCreateArTaxRulesMutation();
  const [updateArTaxRules, { status: updateArTaxRulesStatus }] =
    useUpdateArTaxRulesMutation();

  const headerNavigation = { onClick: onClose };

  const isEditing = !!editingItem;
  const mutationStatus = isEditing
    ? updateArTaxRulesStatus
    : createArTaxRulesStatus;

  const disabledProducts = useMemo(() => {
    if (!editingItem) {
      return productIdsWithExceptions;
    }

    const currentEditingProductIds = editingItem.products.map(
      (p) => p.product_id,
    );
    return productIdsWithExceptions.filter(
      (id) => !currentEditingProductIds.includes(id),
    );
  }, [productIdsWithExceptions, editingItem]);

  const { values, handleChange, handleOnSubmit, setFieldValue, isDirty } =
    useForm({
      initialValues: editingItem ?? initialValues,
      onSubmit: (values) => {
        const payload = {
          description: values.description,
          is_general: false,
          iva_rate: values.iva_rate || 0,
          products: values.products,
        };

        if (editingItem) {
          updateArTaxRules({
            id: editingItem.id,
            payload,
          });
        } else {
          createArTaxRules(payload);
        }
      },
    });
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'iva_rate') {
      const numericValue =
        e.target.value === '' ? null : Number(e.target.value);
      setFieldValue('iva_rate', numericValue);
    } else {
      handleChange(e);
    }
  };

  const handleOnChangeProducts = (products: SelectedProductInterface[]) => {
    setFieldValue('products', products.map(unFormatToSelectedProduct));
  };

  const isFormValid = useMemo(() => {
    const hasDescription =
      values.description && values.description.trim().length > 0;
    const hasIvaRate =
      values.iva_rate !== null && values.iva_rate !== undefined;
    const hasProducts = values.products && values.products.length > 0;

    return hasDescription && hasIvaRate && hasProducts;
  }, [values.description, values.iva_rate, values.products]);

  const handleRemoveProduct = (productId: string) => {
    setFieldValue(
      'products',
      values.products.filter((product) => product.product_id !== productId),
    );
  };

  useToastStatus({
    status: toStatusType(mutationStatus),
    success: isEditing ? t('toastEditException') : t('toastAddException'),
    error: isEditing
      ? t('toastErrorEditException')
      : t('toastErrorAddException'),
    onSuccess: () => onClose(),
  });

  return (
    <ModalAside
      isOpen={isOpen}
      onDidDismiss={onClose}
      headerTop={<HeaderTop navigation={headerNavigation} />}
      headerContent={
        <Title fontSize="h2">{editingItem ? t('editTitle') : t('title')}</Title>
      }
    >
      <Box display="flex" flexDirection="column" gap="4">
        <Card>
          <Card.Header>
            <Title as="h4">{t('name')}</Title>
          </Card.Header>
          <Card.Body>
            <Box display="flex" flexDirection="column" gap="2">
              <FormField.Input
                name="description"
                value={values.description}
                onChange={handleFormChange}
                helpText={t('helpTextName')}
                showHelpText
              />
            </Box>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Title as="h4">{t('aliquotTitle')}</Title>
          </Card.Header>
          <Card.Body>
            <TaxRateInput
              name="iva_rate"
              value={values.iva_rate}
              onChange={handleFormChange}
            />
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Title as="h4">{t('applyTo')}</Title>
          </Card.Header>
          <Card.Body>
            <Box display="flex" flexDirection="column" gap="3">
              <Radio
                as="button"
                name="my-radio"
                label={t('buttonProducts')}
                checked
              />
              {values.products.length > 0 ? (
                <SelectedListProducts
                  selectedProducts={values.products.map(
                    formatToSelectedProduct,
                  )}
                  onRemoveProduct={handleRemoveProduct}
                  onOpenModal={openModal}
                />
              ) : (
                <Link
                  appearance="primary"
                  as="button"
                  textDecoration="none"
                  onClick={openModal}
                >
                  <Icon color="currentColor" source={<PlusCircleIcon />} />
                  {t('selectProducts')}
                </Link>
              )}
            </Box>
          </Card.Body>
        </Card>
      </Box>
      <CancelAndConfirmButtons
        isLoading={mutationStatus === 'pending'}
        confirmText={t('save')}
        isCancelDisabled={mutationStatus === 'pending'}
        isConfirmDisabled={(isEditing && !isDirty) || !isFormValid}
        onCancel={onClose}
        onConfirm={handleOnSubmit}
      />
      {isShowModal && (
        <SelectProductList
          title={t('selectProducts')}
          onClose={closeModal}
          isShow={isShowModal}
          selectedProducts={values.products.map(formatToSelectedProduct)}
          onChange={handleOnChangeProducts}
          disabledProducts={disabledProducts}
          disabledAsSelected
          showVariants
        />
      )}
    </ModalAside>
  );
}

export default TaxExceptionsModal;
